import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Cart upsell card quick add. Copied from stln-quick-add.js and adapted for the
 * cart rail, so the product-card quick add is never affected by changes here.
 *
 * - Single variant (or colour only): the card's Add button submits directly.
 * - Has sizes: Select size swaps the button for the size grid of the selected
 *   colour; picking a size adds it straight away (no second tap).
 * - Colour swatches switch which colour's size grid is shown.
 *
 * Once added, the cart re-renders without this product, so the card leaves the rail.
 */
class StlnUpsellQuickAddComponent extends Component {
  /** @type {((event: Event) => void) | null} */
  #outsidePointerHandler = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    this.#teardownOutsideHandler();
    this.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    super.disconnectedCallback();
  }

  get panel() {
    return this.querySelector('[data-stln-panel]');
  }

  get trigger() {
    return this.querySelector('[data-stln-trigger]');
  }

  get variantInput() {
    return this.querySelector('input[name="id"]');
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  /**
   * Opens the size grid. Bound via on:click on the Select size button.
   * @param {Event} [event]
   */
  open(event) {
    event?.preventDefault();
    event?.stopPropagation();

    const { panel } = this;
    if (!panel || this.isOpen) return;

    for (const other of document.querySelectorAll('stln-upsell-quick-add-component[open]')) {
      if (other !== this && typeof other.close === 'function') other.close();
    }

    panel.hidden = false;
    this.setAttribute('open', '');
    this.trigger?.setAttribute('aria-expanded', 'true');

    this.#outsidePointerHandler = (pointerEvent) => {
      const target = pointerEvent.target;
      if (target instanceof Node && this.contains(target)) return;
      this.close();
    };

    // Deferred so the click that opened the grid does not immediately close it.
    requestAnimationFrame(() => {
      if (this.#outsidePointerHandler) {
        document.addEventListener('pointerdown', this.#outsidePointerHandler);
      }
    });

    this.#activeSizeGroup()?.querySelector('[data-stln-size]:not([disabled])')?.focus();
  }

  /**
   * Closes the size grid. Bound via on:click on the close button.
   * @param {Event} [event]
   */
  close(event) {
    event?.preventDefault();
    event?.stopPropagation();

    const { panel } = this;
    if (!panel) return;

    panel.hidden = true;
    this.removeAttribute('open');
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.#teardownOutsideHandler();
  }

  /**
   * Switches the selected colour: swatch state, colour label, and which size grid shows.
   * @param {Event} event
   */
  selectColor(event) {
    event.preventDefault();
    event.stopPropagation();

    const target = event.target;
    if (!(target instanceof Element)) return;

    const swatch = target.closest('[data-stln-color]');
    if (!(swatch instanceof HTMLButtonElement) || swatch.disabled) return;

    const color = swatch.dataset.stlnColor;

    for (const other of this.querySelectorAll('[data-stln-color]')) {
      other.setAttribute('aria-pressed', String(other === swatch));
    }

    const label = this.querySelector('[data-stln-color-label]');
    if (label && color) label.textContent = color;

    // The card's photo lives next to this component, so swap it for the chosen color's.
    const imageUrl = swatch.dataset.imageUrl;
    if (imageUrl) {
      const image = this.closest('.stln-upsell__card')?.querySelector('.stln-upsell__image');
      if (image instanceof HTMLImageElement) {
        image.removeAttribute('srcset');
        image.src = imageUrl;
        if (swatch.dataset.imageAlt) image.alt = swatch.dataset.imageAlt;
      }
    }

    for (const group of this.querySelectorAll('[data-stln-size-group]')) {
      if (group instanceof HTMLElement) group.hidden = group.dataset.stlnSizeGroup !== color;
    }

    // Colour-only products have no size grid: the swatch itself picks the variant.
    const input = this.variantInput;
    if (input instanceof HTMLInputElement && swatch.dataset.variantId) {
      input.value = swatch.dataset.variantId;
    }
  }

  /**
   * Picks a size and adds it straight to the cart.
   * @param {Event} event
   */
  selectVariant(event) {
    event.preventDefault();
    event.stopPropagation();

    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-stln-size]');
    if (!(button instanceof HTMLButtonElement) || button.disabled || !button.dataset.variantId) return;
    if (this.hasAttribute('adding')) return;

    const input = this.variantInput;
    if (!(input instanceof HTMLInputElement)) return;

    input.value = button.dataset.variantId;
    input.disabled = false;

    for (const size of this.querySelectorAll('[data-stln-size]')) {
      size.setAttribute('aria-pressed', String(size === button));
    }

    this.setAttribute('adding', '');
    this.querySelector('form')?.requestSubmit();

    // A failed add never fires the cart update that would clear this.
    setTimeout(() => this.removeAttribute('adding'), 4000);
  }

  #activeSizeGroup() {
    return this.querySelector('[data-stln-size-group]:not([hidden])');
  }

  #teardownOutsideHandler() {
    if (!this.#outsidePointerHandler) return;
    document.removeEventListener('pointerdown', this.#outsidePointerHandler);
    this.#outsidePointerHandler = null;
  }

  #handleCartUpdate = () => {
    this.removeAttribute('adding');
    if (this.isOpen) this.close();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    this.close();
    if (this.trigger instanceof HTMLElement) this.trigger.focus();
  };
}

if (!customElements.get('stln-upsell-quick-add-component')) {
  customElements.define('stln-upsell-quick-add-component', StlnUpsellQuickAddComponent);
}
